import React, { useRef, useState } from 'react';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { FieldRef } from '@mui/x-date-pickers/models';
import dayjs, { Dayjs } from 'dayjs';

import { ProjectLayoutModel } from '@paragon/contract-library';
import { editLayoutModelProperties } from '@paragon/layout-models';

import { DATE_FORMAT } from '../dueDates';
import { useAppDispatch } from './hooks';

interface Props {
  readonly project: ProjectLayoutModel;
}

interface Draft {
  readonly date: Dayjs | null;
  readonly invalid: boolean; // true if the picker rejected the date
}

export function DueDatePicker({ project }: Props) {
  const dispatch = useAppDispatch();

  const [draft, setDraft] = useState<Draft>();
  const fieldRef = useRef<FieldRef<Dayjs | null>>(null);

  function save({ date, invalid }: Draft) {
    setDraft(undefined);
    if (invalid) return;

    const dueDate = date?.format('YYYY-MM-DD') ?? null;
    if (dueDate === project.dueDate) return;

    dispatch(editLayoutModelProperties(project.guid, { dueDate }));
  }

  /** Whether the field shows a partly typed date, which MUI reports as an empty date rather than an invalid one. */
  function isPartlyTyped() {
    const sections = fieldRef.current?.getSections() ?? [];
    return sections.some((section) => section.value !== '') && sections.some((section) => section.value === '');
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        label="Due date"
        format={DATE_FORMAT}
        value={draft ? draft.date : project.dueDate ? dayjs(project.dueDate) : null}
        onChange={(date, { validationError }) => setDraft({ date, invalid: validationError !== null })}
        onAccept={(date, { source, validationError }) => {
          // typing also fires `onAccept` for every edit with source 'field'; those values should wait for blur or Enter
          if (source === 'view') save({ date, invalid: validationError !== null });
        }}

        slotProps={{
          textField: { size: 'small' },
          field: {
            clearable: true,
            fieldRef,
            shouldRespectLeadingZeros: true,
            onBlur: () => {
              if (draft) save({ date: draft.date, invalid: draft.invalid || isPartlyTyped() });
            },
            onKeyDown: (event) => {
              if (event.key !== 'Enter') return;

              event.preventDefault();
              if (draft && !draft.invalid && !isPartlyTyped()) save(draft);
            },
          },
        }}
      />
    </LocalizationProvider>
  );
}
